/**
 * InstallPage — App installeren (spec §8.8, PWA contract §7).
 *
 * - Uses useInstallPrompt() from '@/pwa' (workstream K): when the browser has
 *   a deferred install prompt, the primary button triggers the native flow.
 * - Platform guidance is honest and manual where no programmatic install
 *   exists: iOS Safari ('Deel' → 'Zet op beginscherm'), Android Chrome
 *   (menu → 'App installeren'), desktop (install icon in the address bar).
 * - The status list checks real browser capabilities — nothing is claimed
 *   that the device does not support.
 */
import { useMemo, useState } from 'react'
import { Button, Card, StatusCard, useToast } from '@/components/ui'
import { CheckIcon, WarningIcon } from '@/components/icons'
import { PageHeader } from '@/components/navigation'
import { useInstallPrompt } from '@/pwa'
import { PATHS } from '@/app/router/paths'
import './settings.css'

type Platform = 'ios' | 'android' | 'desktop'

function detectPlatform(): Platform {
  if (typeof navigator === 'undefined') return 'desktop'
  const ua = navigator.userAgent.toLowerCase()
  if (/iphone|ipod/.test(ua)) return 'ios'
  // iPadOS reports as Macintosh with touch support.
  if (/ipad/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return 'ios'
  }
  if (/android/.test(ua)) return 'android'
  return 'desktop'
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false
  const mediaMatch =
    typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches
  const iosStandalone =
    typeof navigator !== 'undefined' &&
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  return mediaMatch || iosStandalone
}

type Check = { label: string; ok: boolean }

export default function InstallPage() {
  const { canInstall, promptInstall } = useInstallPrompt()
  const { showToast } = useToast()
  const [installing, setInstalling] = useState(false)

  const platform = useMemo(detectPlatform, [])
  const standalone = useMemo(isStandalone, [])

  const checks: Check[] = [
    {
      label: 'Beveiligde verbinding (HTTPS)',
      ok: typeof window !== 'undefined' && window.isSecureContext,
    },
    {
      label: 'Service worker ondersteund',
      ok: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
    },
    {
      label: 'App al geïnstalleerd',
      ok: standalone,
    },
    {
      label: 'Installatieprompt beschikbaar',
      ok: canInstall,
    },
  ]

  const handleInstall = async () => {
    setInstalling(true)
    try {
      await promptInstall()
    } catch {
      showToast({
        message: 'De installatie kon niet worden gestart. Probeer de handmatige stappen hieronder.',
        tone: 'error',
      })
    } finally {
      setInstalling(false)
    }
  }

  return (
    <div className="settings-page">
      <PageHeader title="App installeren" subtitle="RUST op je beginscherm" backTo={PATHS.app.more} />

      {standalone ? (
        <StatusCard
          status="success"
          title="RUST is al geïnstalleerd"
          body="Je gebruikt de app nu vanaf je beginscherm."
        />
      ) : (
        <Card>
          <div className="settings-stack">
            <p className="a11y-preview__body">
              Installeer RUST als app op je beginscherm voor snelle toegang en een rustig volledig
              scherm. Je gegevens en synchronisatie werken hetzelfde als in de browser.
            </p>
            {canInstall && (
              <Button onClick={() => void handleInstall()} loading={installing} fullWidth>
                App installeren
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Manual platform guidance */}
      {!standalone && (
        <section className="settings-section" aria-label="Handmatig installeren">
          <h2 className="settings-section__title">
            {platform === 'ios'
              ? 'Zo installeer je op iPhone of iPad'
              : platform === 'android'
                ? 'Zo installeer je op Android'
                : 'Zo installeer je op deze computer'}
          </h2>
          <Card>
            {platform === 'ios' && (
              <ol className="install-steps">
                <li>Open RUST in Safari (installatie werkt alleen vanuit Safari).</li>
                <li>Tik op de knop ‘Deel’ (het vierkant met de pijl omhoog).</li>
                <li>Scroll naar beneden en tik op ‘Zet op beginscherm’.</li>
                <li>Tik op ‘Voeg toe’. RUST verschijnt nu als app op je beginscherm.</li>
              </ol>
            )}
            {platform === 'android' && (
              <ol className="install-steps">
                <li>Open RUST in Chrome.</li>
                <li>Tik rechtsboven op het menu (drie puntjes).</li>
                <li>
                  Tik op ‘App installeren’ of ‘Toevoegen aan startscherm’ (de naam verschilt per
                  toestel).
                </li>
                <li>Bevestig met ‘Installeren’. RUST verschijnt nu als app op je beginscherm.</li>
              </ol>
            )}
            {platform === 'desktop' && (
              <ol className="install-steps">
                <li>Open RUST in Chrome of Edge.</li>
                <li>Klik op het installatiepictogram rechts in de adresbalk.</li>
                <li>
                  Geen pictogram? Open het browsermenu en kies ‘RUST installeren…’ of ‘Pagina
                  opslaan als app’.
                </li>
                <li>Bevestig met ‘Installeren’.</li>
              </ol>
            )}
          </Card>
        </section>
      )}

      {/* Capability checks */}
      <section className="settings-section" aria-label="Status van deze installatie">
        <h2 className="settings-section__title">Status</h2>
        <Card>
          <ul className="install-checks">
            {checks.map((check) => (
              <li key={check.label} className="install-checks__item">
                <span
                  className={
                    check.ok ? 'install-checks__icon--ok' : 'install-checks__icon--missing'
                  }
                >
                  {check.ok ? <CheckIcon size={18} /> : <WarningIcon size={18} />}
                </span>
                <span>
                  {check.label}: {check.ok ? 'ja' : 'nee'}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </section>
    </div>
  )
}
